/**
 * Winning the z-index fight with third-party storefront widgets.
 *
 * 2147483647 is the maximum 32-bit integer, so it is where every aggressive
 * embed parks itself — Smile.io, Judge.me, Gorgias. We cannot out-number
 * them. At an equal z-index the later element in DOCUMENT ORDER paints on
 * top, and our loader usually runs before theirs, so we lose by default.
 *
 * Found on Lean Living (18 Sep 2026): Smile.io's "Your Journey, Rewarded"
 * prompt is a 380x280 iframe pinned to the bottom of a phone screen at the
 * max z-index, and it sat squarely over our fullscreen mobile panel's input
 * row — elementFromPoint across that whole strip returned Smile's iframe, so
 * the send button could not be tapped at all.
 *
 * The host is deliberately zero-size, `position: static` and creates NO
 * stacking context, so the panel and launcher inside the shadow root compete
 * directly in the ROOT stacking context. That means moving the host is enough:
 * no pointer-events overlay, nothing that could make the widget unclickable.
 */
const DEFAULT_HOST_ID = "react-chat-widget-container";

/**
 * Move the widget host to the end of <body> so it wins a z-index tie.
 *
 * Call when the panel OPENS, not on every render: re-parenting a node with a
 * live shadow root mid-conversation is not worth the risk, and the position
 * only matters while something of ours is on screen.
 *
 * We stay BELOW our own popup by design — the popup service re-appends its
 * own host when a blocking popup opens, landing after us. Final order:
 * popup > chat widget > everything else. The popup's TEASER does not
 * re-append, so it stays behind an open panel.
 */
export function raiseWidgetHost(hostId: string = DEFAULT_HOST_ID): void {
  if (typeof document === "undefined") return;
  const host = document.getElementById(hostId);
  // Only move a host we actually own and that is a direct child of <body>;
  // a merchant who mounted us inside their own container keeps their layout.
  if (!host || host.parentElement !== document.body) return;
  if (document.body.lastElementChild === host) return;
  document.body.appendChild(host);
}
